import React, { useState, useEffect, useRef } from 'react';
import { Loader2, Send, Trash2, MessageCircle } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';

const API = process.env.REACT_APP_BACKEND_URL;

const CommentSection = ({ activityId }) => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [replyTo, setReplyTo] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const replyRef = useRef(null);

  useEffect(() => {
    fetchComments();
  }, [activityId]);

  const fetchComments = async () => {
    try {
      const res = await axios.get(`${API}/api/comments/${activityId}`);
      setComments(res.data.comments || []);
    } catch (err) {
      console.error('Failed to load comments:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim() || submitting) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/comments`, {
        activity_id: activityId,
        text: newComment.trim()
      }, { withCredentials: true });
      setNewComment('');
      fetchComments();
    } catch (err) {
      console.error('Error posting comment:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitReply = async (parentId) => {
    if (!replyText.trim() || submitting) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/comments`, {
        activity_id: activityId,
        text: replyText.trim(),
        parent_id: parentId
      }, { withCredentials: true });
      setReplyText('');
      setReplyTo(null);
      fetchComments();
    } catch (err) {
      console.error('Error posting reply:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (commentId) => {
    try {
      await axios.delete(`${API}/api/comments/${commentId}`, { withCredentials: true });
      fetchComments();
    } catch (err) {
      console.error('Error deleting comment:', err);
    }
  };

  const startReply = (commentId) => {
    setReplyTo(commentId);
    setTimeout(() => replyRef.current?.focus(), 100);
  };

  const timeAgo = (dateStr) => {
    const now = new Date();
    const date = new Date(dateStr);
    const diff = Math.floor((now - date) / 1000);
    if (diff < 60) return t('comment_just_now');
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    return `${Math.floor(diff / 86400)}d`;
  };

  const ReplyItem = ({ reply }) => (
    <div className="flex gap-3 ml-10 mt-3" data-testid={`comment-${reply.id}`}>
      <div className="w-8 h-8 rounded-full bg-[#81B29A]/20 flex items-center justify-center flex-shrink-0 overflow-hidden">
        {reply.user_picture ? (
          <img src={reply.user_picture} alt="" className="w-full h-full object-cover" />
        ) : (
          <span className="text-xs text-[#81B29A] font-medium">{reply.user_name?.charAt(0)?.toUpperCase()}</span>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="bg-white rounded-2xl rounded-tl-sm px-4 py-3">
          <p className="text-sm font-medium text-[#2C302E]">{reply.user_name}</p>
          <p className="text-sm text-[#666B67] mt-0.5">{reply.text}</p>
        </div>
        <div className="flex items-center gap-4 mt-1 px-2">
          <span className="text-xs text-[#666B67]">{timeAgo(reply.created_at)}</span>
          {user && reply.user_id === user.id && (
            <button onClick={() => handleDelete(reply.id)} className="text-xs text-red-400 font-medium">
              <Trash2 className="w-3 h-3" />
            </button>
          )}
        </div>
      </div>
    </div>
  );

  const CommentItem = ({ comment }) => (
    <div className="flex gap-3 mt-4" data-testid={`comment-${comment.id}`}>
      <div className="w-8 h-8 rounded-full bg-[#81B29A]/20 flex items-center justify-center flex-shrink-0 overflow-hidden">
        {comment.user_picture ? (
          <img src={comment.user_picture} alt="" className="w-full h-full object-cover" />
        ) : (
          <span className="text-xs text-[#81B29A] font-medium">{comment.user_name?.charAt(0)?.toUpperCase()}</span>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="bg-white rounded-2xl rounded-tl-sm px-4 py-3">
          <p className="text-sm font-medium text-[#2C302E]">{comment.user_name}</p>
          <p className="text-sm text-[#666B67] mt-0.5">{comment.text}</p>
        </div>
        <div className="flex items-center gap-4 mt-1 px-2">
          <span className="text-xs text-[#666B67]">{timeAgo(comment.created_at)}</span>
          {user && (
            <button onClick={() => startReply(comment.id)} className="text-xs text-[#E07A5F] font-medium" data-testid={`reply-btn-${comment.id}`}>
              {t('comment_reply')}
            </button>
          )}
          {user && comment.user_id === user.id && (
            <button onClick={() => handleDelete(comment.id)} className="text-xs text-red-400 font-medium" data-testid={`delete-comment-${comment.id}`}>
              <Trash2 className="w-3 h-3" />
            </button>
          )}
        </div>
        {replyTo === comment.id && (
          <div className="flex gap-2 mt-2 ml-2" data-testid="reply-form">
            <input
              ref={replyRef}
              type="text"
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSubmitReply(comment.id)}
              placeholder={t('comment_write_reply')}
              className="flex-1 text-sm px-3 py-2 bg-white border border-[#2C302E]/10 rounded-full focus:outline-none focus:ring-1 focus:ring-[#E07A5F]"
              data-testid="reply-input"
            />
            <button
              onClick={() => handleSubmitReply(comment.id)}
              disabled={!replyText.trim() || submitting}
              className="w-8 h-8 rounded-full bg-[#E07A5F] flex items-center justify-center disabled:opacity-50"
              data-testid="reply-submit-btn"
            >
              <Send className="w-3.5 h-3.5 text-white" />
            </button>
          </div>
        )}
        {comment.replies?.map(reply => (
          <ReplyItem key={reply.id} reply={reply} />
        ))}
      </div>
    </div>
  );

  return (
    <div data-testid="comment-section">
      <h3 className="text-lg font-semibold text-[#2C302E] font-['Outfit'] mb-2 flex items-center gap-2">
        <MessageCircle className="w-5 h-5 text-[#E07A5F]" />
        {t('comments_title')} {comments.length > 0 && `(${comments.length})`}
      </h3>

      {/* Post comment form */}
      {user ? (
        <form onSubmit={handleSubmitComment} className="flex gap-2 mb-4" data-testid="comment-form">
          <div className="w-8 h-8 rounded-full bg-[#81B29A]/20 flex items-center justify-center flex-shrink-0 overflow-hidden">
            {user.picture ? (
              <img src={user.picture} alt="" className="w-full h-full object-cover" />
            ) : (
              <span className="text-xs text-[#81B29A] font-medium">{user.name?.charAt(0)?.toUpperCase()}</span>
            )}
          </div>
          <div className="flex-1 flex gap-2">
            <input
              type="text"
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder={t('comment_placeholder')}
              className="flex-1 text-sm px-4 py-2 bg-white border border-[#2C302E]/10 rounded-full focus:outline-none focus:ring-1 focus:ring-[#E07A5F]"
              data-testid="comment-input"
            />
            <button
              type="submit"
              disabled={!newComment.trim() || submitting}
              className="w-8 h-8 rounded-full bg-[#E07A5F] flex items-center justify-center disabled:opacity-50"
              data-testid="comment-submit-btn"
            >
              {submitting ? <Loader2 className="w-3.5 h-3.5 text-white animate-spin" /> : <Send className="w-3.5 h-3.5 text-white" />}
            </button>
          </div>
        </form>
      ) : (
        <p className="text-sm text-[#666B67] mb-4">{t('comment_login_required')}</p>
      )}

      {loading ? (
        <div className="flex justify-center py-6">
          <Loader2 className="w-5 h-5 animate-spin text-[#E07A5F]" />
        </div>
      ) : comments.length > 0 ? (
        <div className="space-y-1">
          {comments.map(comment => (
            <CommentItem key={comment.id} comment={comment} />
          ))}
        </div>
      ) : (
        <p className="text-sm text-[#666B67] text-center py-6">{t('comments_empty')}</p>
      )}
    </div>
  );
};

export default CommentSection;
